/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import {notifySignalView} from '../../common/util/template-state';
import * as _ from 'lodash';

import template from './category-targeting.component.html';
import {ADN_TARGETING_TYPES, SPECIAL_NO_CATEGORY_TARGET} from '../targeting-constants';
import {normalizeTargetingSearchPage} from '../normalize-targeting-search-page';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {convertIabCategoryToString} from '../../../components/util/iab-taxonomy';
import {TargetingSearchApiService} from '../targeting-search-api.service';

const ORIGIN_OPTIONS = {
  REF_DATA: {
    api: ['UPLOAD']
  },
  CONTENT: {
    api: ['SAMPLER']
  },
  IAB: {
    api: ['SAMPLER']
  }
};
const CATEGORY_TYPE_OPTIONS = {
  REF_DATA: {
    api: ['NORMAL', 'IAB']
  },
  CONTENT: {
    api: ['NORMAL']
  },
  IAB: {
    api: ['IAB']
  }
};

@Component({
  standalone: false,
  selector: 'adn-category-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.category;
  @Input() underModel: any;
  @Input() pageType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  inputted: any = {};
  inputs: any[] = [];
  templateState: any = {
    loaded: true,
    complexInput: false
  };
  list: any = {searchResults: [], totalHits: 0, pageStart: 0, pageEnd: 0};
  searchTerm = '';
  originOption = {selection: 'ALL'};
  readonly viewRevision = signal(0);
  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolved: true,
    changePage: () => this.search()
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summary = TargetingHelper.isComplex(this.model, ADN_TARGETING_TYPES.category.id)
        ? (TargetingHelper.getSizeOfTargetingTypes(this.model, ADN_TARGETING_TYPES.category.targets) + ' categories with complex logic')
        : this.inputted.value;
      return {
        vm: _.cloneDeep(this.inputted),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.category,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(private readonly targetingSearchApi: TargetingSearchApiService) {
  }

  ngOnInit(): void {
    this.initAll();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.category.widget, () => this.search(true), copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  search(newSearch?: boolean): void {
    this.searchMeta.resolved = false;

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    const originApi = _.uniq(_.flatten(_.map(_.filter(ORIGIN_OPTIONS, (_oo, key) => {
      return this.originOption.selection === 'ALL' || this.originOption.selection === key;
    }), (oo) => oo.api))).join(';');
    const categoryTypeApi = _.uniq(_.flatten(_.map(_.filter(CATEGORY_TYPE_OPTIONS, (_oo, key) => {
      return this.originOption.selection === 'ALL' || this.originOption.selection === key;
    }), (oo) => oo.api))).join(';');

    const params: any = {
      q: this.searchTerm || '',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1,
      origin: originApi,
      categoryType: categoryTypeApi
    };
    if (!params.q) {
      params.orderBy = 'FREQUENCY';
      params.orderByDirection = 'DESCENDING';
    }

    this.targetingSearchApi.searchCategories(params).subscribe((page) => {
        const list = normalizeTargetingSearchPage(page, {
          pageSize: this.searchMeta.pageSize,
          currentPage: this.searchMeta.currentPage
        });
        _.forEach(_.get(list, 'searchResults') || [], (sr: any) => {
          sr.updatedOrigins = _.uniqBy(sr.origins, (og: string) => {
            return og === 'CRAWLER' || og === 'SAMPLER' ? 'SITE_CONTENT' : og;
          });
          sr.label = convertIabCategoryToString(sr.category);
        });

        if (!this.templateState.complexInput) {
          const trimmedValues = TargetingHelper.trimValues(this);
          _.forEach(_.get(list, ['searchResults'], []), (a: any) => {
            a.selected = _.indexOf(trimmedValues.value, a.category) > -1;
          });
        }
        this.list = list;
        this.searchMeta.resolved = true;
        notifySignalView(this.viewRevision);
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search();
  }

  select(searchResult: any, param: string): void {
    searchResult.selected = true;
    const trimmedValues = TargetingHelper.trimValues(this);
    trimmedValues[param].push(this.deriveIabCategory(searchResult));
    TargetingHelper.assignToInput(this, trimmedValues, undefined, undefined, undefined, undefined);
  }

  addOr(searchResult: any): void {
    this.addEntry(this.deriveIabCategory(searchResult));
  }

  addAnd(searchResult: any): void {
    this.addRow(null, this.deriveIabCategory(searchResult));
  }

  addAndNot(searchResult: any): void {
    this.addNotRow(null, this.deriveIabCategory(searchResult));
  }

  addKeywordAnd(): void {
    this.inputted.valueAnded = this.inputted.valueAnded || [];
    this.inputted.valueAnded.push({value: ''});
  }

  addEntry(entry?: string): void {
    TargetingHelper.checkAndCreateBaseInput(this, 'categories', 'notCategories');
    const input = entry || '';
    this.inputs.push({categories: [{k: input, not: false}], notCategories: []});
  }

  addRow(index?: number | null, entry?: string): void {
    TargetingHelper.checkAndCreateBaseInput(this, 'categories', 'notCategories');
    const input = entry || '';
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].categories.push({k: input, not: false});
  }

  addNotRow(index?: number | null, entry?: string): void {
    TargetingHelper.checkAndCreateBaseInput(this, 'categories', 'notCategories');
    const input = entry || '';
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].notCategories.push({k: input, not: false});
  }

  negateRow(parentIndex: number, index: number, isNot?: boolean): void {
    const param = isNot ? 'notCategories' : 'categories';
    this.inputs[parentIndex][param][index].not = !this.inputs[parentIndex][param][index].not;
  }

  removeRow(parentIndex: number, index: number, isNot?: boolean): void {
    this.inputs[parentIndex][isNot ? 'notCategories' : 'categories'].splice(index, 1);
    if (this.inputs[parentIndex].categories.length === 0 && this.inputs[parentIndex].notCategories.length === 0) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  private deriveIabCategory(searchResult: any): string {
    return searchResult.categoryType === 'IAB' ? this.extractIabCode(searchResult.category) : searchResult.category;
  }

  private extractIabCode(iab: string): string {
    const startIndex = iab.indexOf('[IAB_');
    const endIndex = iab.indexOf(']', startIndex);
    return startIndex > 0 && endIndex > startIndex ? iab.substring(startIndex + 1, endIndex) : iab;
  }

  private initAll(): void {
    this.templateState.complexInput = this.init();
  }

  private init(): boolean {
    TargetingHelper.complexInit(this, ADN_TARGETING_TYPES.category.id, 'categories', 'notCategories');
    let frontPageMethod = '';
    _.forEach(this.model, (entryObj: any) => {
      const cat = _.get(entryObj, 'categories[0]');
      if (cat === SPECIAL_NO_CATEGORY_TARGET) {
        frontPageMethod = 'FRONT';
        return;
      }
      if (cat === 'NOT ' + SPECIAL_NO_CATEGORY_TARGET) {
        frontPageMethod = 'ANY';
      }
    });
    TargetingHelper.simpleInit(this, ADN_TARGETING_TYPES.category.apiProp);
    this.inputted.frontPageMethod = frontPageMethod || '';
    return !!TargetingHelper.isComplex(this.model, ADN_TARGETING_TYPES.category.id);
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    const matchingGroup = _.get(this.model, [0, 'matchingGroup'], 0);
    this.model.length = 0;

    const specialStuff = () => {
      if (this.inputted.frontPageMethod === 'ANY') {
        this.model.push({categories: ['NOT ' + SPECIAL_NO_CATEGORY_TARGET]});
      }
      if (this.inputted.frontPageMethod === 'FRONT') {
        let hasSpecialAlready = false;
        _.forEach(this.model, (catEntry: any) => {
          _.forEach(catEntry.categories, (cArray: string) => {
            hasSpecialAlready = hasSpecialAlready || cArray === SPECIAL_NO_CATEGORY_TARGET;
          });
        });
        if (!hasSpecialAlready) {
          this.model.push({categories: [SPECIAL_NO_CATEGORY_TARGET]});
        }
      }
    };

    if (this.pageType === 'optionalProduct') {
      TargetingHelper.assignAllTheValuesToSimpleModel(this, ADN_TARGETING_TYPES.category.apiProp);
    } else if (this.templateState.complexInput) {
      TargetingHelper.assignComplexValuesToNewModel(this, 'categories', 'notCategories');
    } else {
      TargetingHelper.assignAllTheValuesToPipedModel(this, ADN_TARGETING_TYPES.category.apiProp);
    }
    specialStuff();
    this.init();
    if (matchingGroup > 0) {
      _.forEach(this.model, (m: any) => {
        m.matchingGroup = matchingGroup;
      });
    }
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.initAll();
  }
}
